/**
 * Insert - Accounting Invoices — bulk-import grid dialog cho Procurement Invoice.
 * Access `frmProcurementQryInvoiceSuggestAdd` (Caption "Insert - Accounting Invoices"),
 * subform `frmProcurementQryInvoiceSuggest`.
 *
 * Mở từ toolbar "Accounting - Input Invoices" của ProcurementInvoiceListView. Load
 * danh sách hoá đơn gợi ý (suggest-hoadon) vào grid có cột tick "To be Edited" +
 * cột contract_no editable (Autocomplete). User tick các dòng + hoàn thiện Contract
 * No rồi Save → BE upsert vào bảng invoice.
 *
 * Grid theo template dataList (/convert-table): app-dataframe + gold header + global
 * Search + per-column ▽ filter + sort + resize + TableVirtuoso + Access formatters.
 * Vỏ dialog theo /convert-form: dialogStyle helpers, Save/Cancel top-right, saving
 * guard + backdrop-proof onClose. KHÔNG fullScreen.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useInvoiceAllContractsLookupQuery,
  useListInvoiceSuggestHoadonQuery,
  useSaveInvoiceSuggestHoadonMutation,
} from '@/store/api/procurementInvoiceApi'
import type {
  InvoiceLookupItem,
  InvoiceSuggestHoadonItem,
  InvoiceSuggestHoadonSaveRow,
} from '@/types/api'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { titleCaseName } from '@/utils/text'
import { getErrorMessage } from '@/utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
} from '../purchase/dialogStyle'

interface Props {
  open: boolean
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Format date DD-MMM-YYYY. */
const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

/** Format numeric: 2 decimals (Access Standard). */
const formatAccessNumber = (value: unknown): string => {
  if (value == null || value === '' || typeof value !== 'number' || !Number.isFinite(value)) return ''
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const TICK_COL_WIDTH = 60

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = {
  key: keyof InvoiceSuggestHoadonItem
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  proper?: boolean
  width?: number
}

// Thứ tự + caption theo ColumnOrder của subform frmProcurementQryInvoiceSuggest.
// checked_row (ColumnOrder 0) → cột tick riêng; contract_no (1) editable Autocomplete.
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no', label: 'Contract No', width: 150 }, // 1 — editable (popup rộng hơn cột)
  { key: 'note', label: 'Note', wrap: true, width: 180 }, // 2
  { key: 'invoice_no', label: 'Invoice No', width: 140 }, // 3
  { key: 'cp_code', label: 'CP Code', width: 100 }, // 4
  { key: 'cp_name', label: 'CP Name', wrap: true, proper: true, width: 200 }, // 5
  { key: 'ngay_hoa_don', label: 'Invoice Date', kind: 'date', width: 120 }, // 6
  { key: 'price', label: 'Price', kind: 'number', align: 'right', width: 100 }, // 7
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right', width: 100 }, // 8
  { key: 'total_amount_without_vat', label: 'Amount (excl. VAT)', kind: 'number', align: 'right', width: 130 }, // 9
  { key: 'total_vat_amount', label: 'VAT Amount', kind: 'number', align: 'right', width: 120 }, // 10
  { key: 'total_amount', label: 'Total Amount', kind: 'number', align: 'right', width: 130 }, // 11
  { key: 'product_note', label: 'Product Note', wrap: true, width: 480 }, // 12
  { key: 'seller_name', label: 'Seller Name', wrap: true, proper: true, width: 260 }, // 13
  { key: 'trang_thai_hoa_don', label: 'Invoice Status', filter: 'checkboxes', width: 120 }, // 15
  { key: 'phan_loai_chung_tu', label: 'Document Type', filter: 'checkboxes', width: 130 }, // 16
  { key: 'invoice_id', label: 'Invoice ID', width: 280 }, // 17 — mã hoá đơn điện tử (hash dài)
]

/** Flattened grid row = item fields + edited contract_no + stable key. */
type GridRow = InvoiceSuggestHoadonItem & { __key: string }

/** Context truyền cho custom TableVirtuoso components (giữ chúng stable). */
type VtxContext = { displayRows: GridRow[]; ticked: Set<string>; minTableWidth: number }
type VtxComponentProps = { context?: VtxContext } & Record<string, unknown>

const cellValue = (row: GridRow, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'number') return formatAccessNumber(val)
  if (col.proper && typeof val === 'string') return titleCaseName(val)
  return String(val)
}

/** Editable state per row: item gốc + contract_no/note user chỉnh + key ổn định. */
interface EditableRow {
  key: string
  item: InvoiceSuggestHoadonItem
  contractNo: string
  note: string
}

export default function ProcurementInvoiceAccountingDialog({ open, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()

  const { data, isFetching, isError, error, refetch } = useListInvoiceSuggestHoadonQuery(undefined, {
    skip: !open,
  })
  const { data: lookup } = useInvoiceAllContractsLookupQuery(undefined, { skip: !open })
  const [saveRows, { isLoading: isSaving }] = useSaveInvoiceSuggestHoadonMutation()

  const contractOptions = useMemo<InvoiceLookupItem[]>(() => lookup?.items ?? [], [lookup])

  // Local editable copy — reset mỗi lần dialog mở / data reload.
  const [rows, setRows] = useState<EditableRow[]>([])
  const [ticked, setTicked] = useState<Set<string>>(new Set())

  // dataList — global Search + per-column filter + sort + resize (/convert-table).
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof GridRow> | null>(null)
  const { getWidth, startResize } = useColumnWidths('procurement.invoice.accounting.grid')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof InvoiceSuggestHoadonItem
    position: ColumnFilterAnchorPosition
  } | null>(null)

  useEffect(() => {
    if (!open) {
      setRows([])
      setTicked(new Set())
      return
    }
    const items = data?.items ?? []
    setRows(
      items.map((item, i) => ({
        key: `${item.invoice_id ?? ''}|${item.invoice_no ?? ''}|${i}`,
        item,
        contractNo: item.contract_no ?? '',
        note: item.note ?? '',
      })),
    )
    setTicked(new Set())
    setSearchTerm('')
    columnFilters.clearAll()
    setSortState(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data])

  useEffect(() => {
    if (isError) snack.error(getErrorMessage(error, 'Failed to load accounting invoices'))
  }, [isError, error, snack])

  const setContractNo = (key: string, value: string) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, contractNo: value } : r)))

  const setNote = (key: string, value: string) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, note: value } : r)))

  const toggleTick = (key: string) =>
    setTicked((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  // Flatten → filter (Search + per-column) → sort. contract_no dùng giá trị đã sửa.
  const gridRows = useMemo<GridRow[]>(
    () => rows.map((r) => ({ ...r.item, contract_no: r.contractNo, note: r.note, __key: r.key })),
    [rows],
  )
  const filteredRows = useMemo(
    () =>
      gridRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [gridRows, searchTerm, columnFilters.filters],
  )
  const displayRows = useMemo(
    () => sortTableRows(filteredRows, sortState, TABLE_COLUMNS),
    [filteredRows, sortState],
  )

  const toggleSort = (key: keyof GridRow) => setSortState((prev) => toggleTableSort(prev, key))

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    filterPopover.close()
  }

  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, gridRows) : 'text'),
    [popoverColumn, gridRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(gridRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, gridRows],
  )

  // Select-all tick reflects the currently displayed (filtered) rows only.
  const displayedKeys = useMemo(() => displayRows.map((r) => r.__key), [displayRows])
  const allTicked = displayedKeys.length > 0 && displayedKeys.every((k) => ticked.has(k))
  const someTicked = displayedKeys.some((k) => ticked.has(k)) && !allTicked
  const toggleAll = () =>
    setTicked((prev) => {
      const next = new Set(prev)
      if (displayedKeys.every((k) => next.has(k))) displayedKeys.forEach((k) => next.delete(k))
      else displayedKeys.forEach((k) => next.add(k))
      return next
    })

  const minTableWidth = useMemo(
    () =>
      TICK_COL_WIDTH +
      TABLE_COLUMNS.reduce((sum, c) => sum + (getWidth(String(c.key)) ?? c.width ?? 120), 0),
    [getWidth],
  )

  // Stable component types cho TableVirtuoso — nếu tạo mới mỗi render (inline),
  // React remount cả <table> mỗi keystroke → input Note/Contract mất focus.
  // Giá trị động (rows/ticked/width) truyền qua `context` để giữ reactivity.
  const vtxComponents = useMemo(
    () =>
      ({
        Table: ({ context, ...props }: VtxComponentProps) => (
          <Table
            {...props}
            size="small"
            stickyHeader
            sx={{ minWidth: context?.minTableWidth, width: '100%' }}
          />
        ),
        TableHead: (({ context, ...props }: VtxComponentProps) => {
          void context
          return <TableHead {...props} component="thead" />
        }) as never,
        TableBody: (({ context, ...props }: VtxComponentProps) => {
          void context
          return <TableBody {...props} component="tbody" />
        }) as never,
        TableRow: ({ context, ...props }: VtxComponentProps) => {
          const idx = Number((props as Record<string, unknown>)['data-item-index'])
          const row = context && Number.isFinite(idx) ? context.displayRows[idx] : null
          return <TableRow {...props} hover selected={row != null && !!context?.ticked.has(row.__key)} />
        },
      }) as never,
    [],
  )
  const vtxContext = useMemo<VtxContext>(
    () => ({ displayRows, ticked, minTableWidth }),
    [displayRows, ticked, minTableWidth],
  )

  const handleSave = async () => {
    if (!canEdit) return
    const checked = rows.filter((r) => ticked.has(r.key))
    if (checked.length === 0) {
      snack.warning('Please choose a record!')
      return
    }
    const payload: InvoiceSuggestHoadonSaveRow[] = checked.map((r) => ({
      invoice_no: r.item.invoice_no ?? null,
      contract_no: r.contractNo || null,
      cp_code: r.item.cp_code ?? null,
      ngay_hoa_don: r.item.ngay_hoa_don ?? null,
      price: r.item.price ?? null,
      quantity: r.item.quantity ?? null,
      total_amount_without_vat: r.item.total_amount_without_vat ?? null,
      total_vat_amount: r.item.total_vat_amount ?? null,
      note: r.note || null,
    }))
    try {
      const res = await saveRows({ rows: payload }).unwrap()
      snack.success(res.data?.message ?? 'Saved')
      onSaved()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e))
    }
  }

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const disabledSave = !canEdit || isSaving || isFetching || ticked.size === 0

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (isSaving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography
          component="span"
          noWrap
          title="Insert - Accounting Invoices"
          sx={dialogTitleTextSx}
        >
          Insert - Accounting Invoices
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={disabledSave}
            sx={dialogHeaderBtnSx}
          >
            {isSaving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={isSaving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for Invoice.
          </Alert>
        )}
        {isError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Could not load accounting invoices.{' '}
            <Button size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          </Alert>
        )}

        {/* Records grid — dataList template (/convert-table). Cột "To be Edited"
            = multi-row select (Access checked_row); Contract No editable. */}
        <FilterBar
          hasActiveFilters={hasActiveFilters}
          onClearAll={clearAllFilters}
          activeFilterCount={activeFilterCount}
          rightSlot={
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              (Only edit Ticked Rows) — {ticked.size} selected
            </Typography>
          }
        >
          <FilterButton
            label="Search"
            active={!!searchTerm.trim()}
            onClick={(e) => filterPopover.open('search', e)}
          />
        </FilterBar>

        <Divider sx={{ mb: 0.5 }} />

        <Box className="app-dataframe" sx={{ height: { xs: 340, sm: 460 } }}>
          <Box
            className="app-dataframe-toolbar"
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}
          >
            {isFetching && <CircularProgress size={15} />}
            <Typography variant="caption">Total: {filteredRows.length}</Typography>
            <Typography variant="caption">Loaded: {rows.length}</Typography>
          </Box>

          {/* Global Search popover */}
          <FilterPopover
            open={filterPopover.state !== null}
            anchorEl={filterPopover.state?.anchor ?? null}
            onClose={() => filterPopover.close()}
          >
            {filterPopover.isOpen('search') && (
              <SearchFilterContent
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Contract, invoice no, seller, CP..."
                onClear={() => filterPopover.close()}
              />
            )}
          </FilterPopover>

          {/* Per-column filter popover (1 instance) */}
          <ColumnFilterPopover
            open={colPopover !== null}
            anchorPosition={colPopover?.position ?? null}
            onClose={() => setColPopover(null)}
            label={popoverColumn?.label ?? ''}
            kind={popoverKind === 'none' ? 'text' : popoverKind}
            value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
            onChange={(v) => {
              if (!colPopover) return
              columnFilters.setFilter(String(colPopover.key), v)
            }}
            options={popoverOptions}
          />

          {!isFetching && rows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary', flex: 1 }}>
              <Typography variant="body2">No accounting invoices to import.</Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 200, overflow: 'hidden' }}>
              <TableVirtuoso
                style={{ height: '100%' }}
                data={displayRows}
                context={vtxContext}
                components={vtxComponents}
                fixedHeaderContent={() => (
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      className="app-table-header-cell"
                      style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH }}
                    >
                      <Checkbox
                        size="small"
                        checked={allTicked}
                        indeterminate={someTicked}
                        disabled={!canEdit || displayRows.length === 0}
                        onChange={toggleAll}
                        title="Tick all"
                      />
                    </TableCell>
                    {TABLE_COLUMNS.map((col) => {
                      const key = String(col.key)
                      const w = getWidth(key) ?? col.width
                      return (
                        <TableCell
                          key={key}
                          align={col.align}
                          className="app-table-header-cell"
                          onClick={() => toggleSort(col.key)}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            userSelect: 'text',
                            position: 'relative',
                          }}
                        >
                          {col.label}
                          {getTableSortSuffix(sortState, col.key)}
                          <ColumnFilterIcon
                            active={columnFilters.isActive(key)}
                            onClick={(position) => setColPopover({ key: col.key, position })}
                            ariaLabel={`Filter ${col.label}`}
                          />
                          <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )}
                itemContent={(_, row) => (
                  <>
                    <TableCell
                      padding="checkbox"
                      style={{ width: TICK_COL_WIDTH, minWidth: TICK_COL_WIDTH }}
                    >
                      <Checkbox
                        size="small"
                        checked={ticked.has(row.__key)}
                        onChange={() => toggleTick(row.__key)}
                        disabled={!canEdit}
                      />
                    </TableCell>
                    {TABLE_COLUMNS.map((col) => {
                      const key = String(col.key)
                      const w = getWidth(key) ?? col.width

                      // Editable Contract No — Autocomplete (skill combo style).
                      if (col.key === 'contract_no') {
                        const selectedOption =
                          contractOptions.find((o) => o.value === row.contract_no) ??
                          (row.contract_no
                            ? { value: String(row.contract_no), label: String(row.contract_no) }
                            : null)
                        const merged =
                          selectedOption &&
                          !contractOptions.some((o) => o.value === selectedOption.value)
                            ? [selectedOption, ...contractOptions]
                            : contractOptions
                        return (
                          <TableCell
                            key={key}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{ py: 0.25, verticalAlign: 'middle' }}
                          >
                            <Autocomplete
                              size="small"
                              options={merged}
                              value={selectedOption}
                              // Ô input sau khi chọn chỉ hiện code (contract_no)...
                              getOptionLabel={(o) => (o ? String(o.value ?? '') : '')}
                              // ...nhưng dropdown hiện "CODE — Name" để dễ tra.
                              renderOption={(props, o) => {
                                const code = String(o.value ?? '')
                                const label = String(o.label ?? '')
                                return (
                                  <li {...props} key={code} style={{ whiteSpace: 'nowrap' }}>
                                    {code === label || !label ? code : `${code} — ${label}`}
                                  </li>
                                )
                              }}
                              // Popup rộng hơn cột (fit nội dung, min 360px) để coi ngang cả list.
                              slotProps={{
                                popper: {
                                  placement: 'bottom-start',
                                  style: { width: 'fit-content', minWidth: 360 },
                                },
                              }}
                              isOptionEqualToValue={(a, b) => a.value === b.value}
                              onChange={(_e, v) => setContractNo(row.__key, v?.value ?? '')}
                              disabled={!canEdit}
                              renderInput={(params) => (
                                <TextField {...params} size="small" placeholder="Contract No" variant="standard" />
                              )}
                            />
                          </TableCell>
                        )
                      }

                      // Editable Note (Access ERPNote(EDIT)).
                      if (col.key === 'note') {
                        return (
                          <TableCell
                            key={key}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{ py: 0.25, verticalAlign: 'middle' }}
                          >
                            <TextField
                              size="small"
                              variant="standard"
                              fullWidth
                              multiline
                              value={row.note ?? ''}
                              onChange={(e) => setNote(row.__key, e.target.value)}
                              disabled={!canEdit}
                              placeholder="Note"
                            />
                          </TableCell>
                        )
                      }

                      const val = cellValue(row, col)
                      const wrap = col.wrap || getWidth(key) != null
                      return (
                        <TableCell
                          key={key}
                          align={col.align}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={
                            wrap
                              ? { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'middle' }
                              : { verticalAlign: 'middle' }
                          }
                        >
                          <Box
                            component="span"
                            className={wrap ? 'app-table-cell-wrap' : 'app-table-cell-text'}
                            title={wrap ? undefined : val || '-'}
                          >
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    })}
                  </>
                )}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
